import React, { useEffect, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault } from "use-query-params";

import { Key, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/lib/table/interface";

import { Page } from "../../components/page";
import { PageHeader } from "../../components/page_header";
import { Searcher } from "../../components/searcher";
import { CurrencySelector, DateRange, MarketSelector } from "../../components/selectors";
import { bookingsFetchRequest, reportGeneratorTaskCreateRequest } from "../../store/bookings/actions";
import { BookingItem, BookingState } from "../../store/bookings/types";
import { ReportFormat } from "../../store/common_types";
import { selectCurrency } from "../../store/currencies/actions";
import { RootState } from "../../store/root_reducer";
import { enumParams } from "../../utils/enum_params";
import { MomentParam } from "../../utils/moment_param";

import { DataTable } from "./components";

export const BookingsPage: React.FC = () => {
  const { t } = useTranslation("bookings_page");

  const bookingsState = useSelector((state: RootState) => state.Bookings);
  const markets = useSelector((state: RootState) => state.Markets.markets);
  const selectedGate = useSelector((state: RootState) => state.User.selectedGate);
  const currency = useSelector((state: RootState) => state.Currencies.selectedCurrency);

  const [refresh, setRefresh] = useState(true);
  const [state, setState] = useQueryParams({
    from: withDefault(MomentParam, moment.utc().startOf("day").subtract(1, "week").add(1, "day")),
    to: withDefault(MomentParam, moment.utc().startOf("day")),
    market: withDefault(StringParam, undefined),
    pageSize: withDefault(NumberParam, 10),
    page: withDefault(NumberParam, 1),
    ordering: withDefault(StringParam, "-createdAt"),
    state: withDefault(
      enumParams<BookingState[], BookingState>(ArrayParam, ["cancelled", "paid", "processing"]),
      []
    ),
    search: withDefault(StringParam, undefined),
  });

  const dispatch = useDispatch();
  useEffect(() => {
    setState((currentState) => currentState, "replaceIn");
  }, [setState]);

  useEffect(() => {
    dispatch(bookingsFetchRequest({ ...state, currency, gateId: selectedGate.id }));
  }, [dispatch, state, currency, selectedGate, refresh]);

  const handleSelectDatesRange = (selectedRange: DateRange) => {
    setState((currentState) => ({ ...currentState, ...selectedRange, page: 1 }));
  };
  const handleMarketSelect = (market?: string) => {
    setState((currentState) => ({ ...currentState, market }));
  };
  const handleCurrencySelect = (selectedCurrency: string): void => {
    dispatch(selectCurrency({ currency: selectedCurrency }));
  };
  const handleSearch = (value: string) => {
    setState((currentState) => ({ ...currentState, search: value || undefined, page: 1 }));
  };

  const handleReport = (reportFormat: ReportFormat) => {
    dispatch(reportGeneratorTaskCreateRequest({ ...state, reportFormat, currency, gateId: selectedGate.id }));
  };
  const handleReload = () => {
    setRefresh((prevState) => !prevState);
  };

  const handleTableChange = (
    { current, pageSize }: TablePaginationConfig,
    filters: Record<string, (Key | boolean)[] | null>,
    sorter: SorterResult<BookingItem> | SorterResult<BookingItem>[],
    { action }: TableCurrentDataSource<BookingItem>
  ) => {
    switch (action) {
      case "paginate": {
        if (current && pageSize) {
          setState((currentState) => ({
            ...currentState,
            page: currentState.pageSize !== pageSize ? 1 : current,
            pageSize,
          }));
        }
        break;
      }
      case "filter": {
        const stateFilters = filters.state || [];
        if (state.state.toString() !== stateFilters.toString()) {
          setState((currentState) => ({ ...currentState, ...filters, page: 1 }));
        }
        break;
      }
      case "sort": {
        if (!Array.isArray(sorter)) {
          const { field, order } = sorter;
          const ordering = `${order === "descend" ? "-" : ""}${field}`;
          setState((currentState) => ({ ...currentState, ordering }));
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <Page title={t("page.title")} description={t("page.description")} reportTTL={730} report="bookings">
      <PageHeader
        onReload={handleReload}
        dateRange={{ from: state.from, to: state.to }}
        isLoading={bookingsState.isLoading}
        onDateRangeSelect={handleSelectDatesRange}
        onDownloadReport={handleReport}
        reportDownloadDisabled={bookingsState.count === 0}
      >
        <MarketSelector markets={markets} value={state.market} onSelect={handleMarketSelect} />
        <CurrencySelector value={currency} onSelect={handleCurrencySelect} />
        <Searcher
          onSearch={handleSearch}
          loading={bookingsState.isLoading}
          examples={["R27223", "32492012", "7128027675"]}
          defaultValue={state.search}
          placeholder={t("searcher.placeholder")}
        />
      </PageHeader>
      <DataTable
        loading={bookingsState.isLoading}
        totalBookings={bookingsState.count}
        dataSource={bookingsState.items}
        currentPage={state.page}
        pageSize={state.pageSize}
        onChange={handleTableChange}
        filtered={{ state: state.state }}
        ordering={state.ordering}
        currentCurrency={currency}
      />
    </Page>
  );
};
